import { ReactNode, useRef, useState } from 'react';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

import { FetchOptions } from './interfaces';
import { fetchWithTokenRefresh } from './fetchWithTokenRefresh';
import { ApiCalls } from './apiCalls';
import { handleResponseError } from './handleResponseError';
import { useHandleApiError } from './useHandleApiError';

interface Props<T> {
  pathname: string;
  options?: FetchOptions;
  onSuccess?: (data: T) => void;
  onError?: (e: Error) => void;
  onFinish?: () => void;
}

export const useFetch = <T = unknown>({ pathname, options, onSuccess, onError, onFinish }: Props<T>) => {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | ReactNode>();
  const currentOptions = useRef<FetchOptions | undefined>(options);
  const handleApiError = useHandleApiError();

  const fetchData = async (fetchOptions = options) => {
    const handleFinally = () => {
      setLoading(false);
      if (onFinish) onFinish();
    };
    try {
      currentOptions.current = fetchOptions;
      setLoading(true);
      setError(undefined);
      const response = await fetchWithTokenRefresh(pathname, fetchOptions);
      const httpSuccessStatus = response.status >= 200 && response.status < 300;
      if (httpSuccessStatus) {
        const jsonData = await response.json();
        setData(jsonData);
        if (onSuccess) onSuccess(jsonData);
        handleFinally();
        return;
      }
      await handleResponseError(response);
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      handleApiError({
        message: e.message,
        handleNonBusinessError: message => {
          setError(message);
        }
      });
      if (onError) onError(e);
      handleFinally();
    }
  };

  useDeepCompareEffectNoCheck(() => {
    fetchData();
    return () => {
      const abortApiPendingRequests = () => {
        ApiCalls.getInstance().abortControllers();
      };
      abortApiPendingRequests();
    };
  }, []);

  return { loading, error, data, refresh: fetchData, previousOptions: currentOptions.current };
};
